import { FormLocationDataLayer,
  PageViewEventParams } from '../hooks/dataLayerHooks/dataLayerV2Hooks';
import { Currency } from './apiEnums';

export enum DataLayerProductCategory {
  ACTIVATION = 'activation',
  ADDITIONAL = 'bijproduct',
  DIGITAL = 'voucher',
  PHYSICAL = 'fysiek',
}

export enum DataLayerProductCategoryV2 {
  ADDITIONAL = 'bijproduct',
  DIGITAL = 'digitaal',
  PHYSICAL = 'fysiek',
}

export enum AffiliationDataLayerParam {
  ACTIVATION = 'activation',
  REGULAR = 'regular',
}

export enum DataLayerPurchasePaymentType {
  ACTIVATION = 'activation',
  REGULAR = 'regular',
  REORDER = 'reorder',
}

export enum EventTypes {
  addPaymentInfo = 'add_payment_info',
  addShippingInfo = 'add_shipping_info',
  addToCart = 'addToCart',
  addToCart2 = 'addToCart2',
  addToCartV2 = 'add_to_cart',
  beginCheckout = 'begin_checkout',
  checkout = 'checkout',
  dataReady = 'data_ready',
  formSubmitQuotation = 'form_submit_quotation',
  login = 'login',
  onlineOrder = 'onlineOrder',
  pageView = 'page_view',
  productClick = 'productClick',
  productDetail = 'productDetail',
  purchase = 'purchase',
  redeemOnlineOrder = 'redeemOnlineOrder',
  removeFromCart = 'removeFromCart',
  removeFromCartV2 = 'remove_from_cart',
  search = 'search',
  searchBoxClick = 'searchbox_click',
  selectItem = 'select_item',
  selectedItemVariantAmount = 'select_item_variant_amount',
  selectedItemVariantGiftPacking = 'select_item_variant_gift_packing',
  selectedItemVariantPackage = 'select_item_variant_package',
  selectedItemVariantShipping = 'select_item_variant_shipping',
  viewCart = 'view_cart',
  viewItem = 'view_item',
  viewItemList = 'view_item_list',
}

export enum ItemListID {
  BLOG_ARTICLE_PAGE = 'blog_article',
  CATEGORY_OVERVIEW_PAGE = 'category_overview_page',
  FEATURED_CATEGORY_OVERVIEW_PAGE = 'featured_category_overview_page',
  GIVING_MOMENTS_PAGE = 'giving_moments_page',
  HEADER_MENU = 'header_menu',
  LANDING_PAGE = 'landing_page',
  MOST_CHOSEN_HOME_PAGE = 'most_chosen_home_page',
  PRODUCTS_OVERVIEW_PAGE = 'products_overview_page',
  PRODUCT_DETAILS_PAGE_RELATED_CARDS = 'product_details_page_related_cards',
  PRODUCT_DETAILS_PAGE_SIMILAR_PRODUCTS = 'product_details_page_similar_products',
  PROMOTED_GIFTCARDS_HOME_PAGE = 'promoted_giftcards_home_page',
  REDEEM_PRODUCTS_OVERVIEW_PAGE = 'redeem_products_overview_page',
  REORDER_PRODUCTS_OVERVIEW_PAGE = 'reorder_products_overview_page',
  SEARCH_RESULTS = 'search_results',
  SEARCH_SUGGESTIONS = 'search_suggestions',
}

export type DataLayerProduct = {
  id: string;
  name: string;
};
export type DataLayerProductV2 = {
  item_id: string;
  item_name: string;
};
export type DataLayerViewItem = DataLayerProductV2 & {
  currency: Currency;
  item_category: DataLayerProductCategoryV2;
  price: number;
  quantity: number;
};
export type DataLayerViewItemList = Omit<DataLayerViewItem, 'price' | 'currency'> & {
  index: number;
  item_list_id: string;
  item_list_name: string;
};
export type DataLayerAddToCartItem = DataLayerProductV2 & {
  currency: Currency;
  item_category: DataLayerProductCategoryV2;
  price: number;
  quantity: number;
};

export type DataLayerClickProduct = DataLayerProduct & { position: number };
export type DataLayerCheckoutProduct = DataLayerProduct & {
  category: DataLayerProductCategory | null;
  currency?: Currency;
  price?: string | null;
  quantity?: number;
};

export type DataLayerRedeemCheckoutProduct = DataLayerProduct & {
  category: string[];
  price: string;
  quantity: number;
};

type DataLayerEventBase = {
  event: EventTypes;
};

export type DataLayerProductClickEvent = {
  ecommerce: {
    click: {
      actionField?: { checkout: string };
      products: DataLayerClickProduct[];
    };
  };
} & DataLayerEventBase;

export type DataLayerSearchEvent = {
  count_of_search_results: number;
  search_term: string;
} & DataLayerEventBase;

export type DataLayerSearchBoxClickedEvent = {
  page_location: string;
} & DataLayerEventBase;

export type DataLayerProductDetailEvent = {
  ecommerce: {
    detail: {
      actionField?: { checkout: string };
      products: DataLayerProduct[];
    };
  };
} & DataLayerEventBase;

export type DataLayerAddEvent = {
  ecommerce: {
    add: {
      actionField: {
        checkout?: string;
      };
      products: DataLayerCheckoutProduct[];
    };
    currencyCode: Currency;
  };
} & DataLayerEventBase;

export type DataLayerRemoveEvent = {
  ecommerce: {
    currencyCode: Currency;
    remove: {
      products: DataLayerCheckoutProduct[];
    };
  };
} & DataLayerEventBase;

export type DataLayerRedeemRemoveEvent = {
  ecommerce: {
    actionField: { checkout: string };
    currencyCode: Currency;
    remove: {
      products: DataLayerCheckoutProduct[];
    };
  };
} & DataLayerEventBase;

export type DataLayerCheckoutEvent = {
  ecommerce: {
    checkout: {
      actionField: { checkout?: string; option: string; step: number };
      products: DataLayerCheckoutProduct[];
    };
  };
} & DataLayerEventBase;

export type DataLayerViewItemEvent = {
  ecommerce: {
    items: DataLayerViewItem[];
  };
  event: EventTypes.viewItem;
};

export type DataLayerSuccessfulOrderEvent = {
  ecommerce: {
    purchase: {
      actionField: {
        affiliation: string;
        billingEmail?: string | null;
        checkout?: string;
        id: string;
        revenue: string;
        shipping: string;
        tax: string;
      };
      products: DataLayerCheckoutProduct[];
    };
  };
} & DataLayerEventBase;

export type DataLayerProductItemV2 = {
  currency: Currency;
  item_category: DataLayerProductCategoryV2;
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
};

export type DataLayerSelectItem = Omit<DataLayerProductItemV2, 'price' | 'currency'> & {
  index: number;
  item_list_id: string;
  item_list_name: string;
};

export type DataLayerLoginUserSuccessfulEvent = {
  event: EventTypes;
  loginMethod: string;
};

export type DataLayerAddShippingInfoEvent = {
  ecommerce: {
    currency: Currency;
    items: DataLayerProductItemV2[];
    value: number;
  };
  enhanced_data: {
    name: string;
    postal_code?: string;
  };
  event: EventTypes.addShippingInfo;
  };

export type DataLayerViewCartEvent = {
    ecommerce: {
      currency: Currency;
      items: DataLayerProductItemV2[];
      shipping: number;
      value: number;
    };
    event: EventTypes.viewCart;
  };

export type DataLayerAddPaymentInfoEvent = {
  ecommerce: {
    currency: Currency;
    items: DataLayerProductItemV2[];
    payment_type: string;
    value: number;
  };
  event: EventTypes.addPaymentInfo;
};

export type DatalayerBeginCheckoutEvent = {
  ecommerce: { items: DataLayerProductItemV2[] };
  event: EventTypes.beginCheckout;
};

export type DataLayerPurchaseEvent = {
  client_type: string;
  companyName: string;
  ecommerce: {
    currency: Currency;
    items: DataLayerProductItemV2[];
    shipping: number;
    transaction_id: string;
    value: number;
  };
  email: string;
  event: EventTypes.purchase;
  lastName: string;
  name: string;
  payment_type: DataLayerPurchasePaymentType;
  phoneNumber: string;
};

export type DataLayerPageViewEvent = {
  event: EventTypes.pageView;
  page: PageViewEventParams;
};

export type DataLayerViewItemListEvent = {
  ecommerce: { items: DataLayerViewItemList[] };
  event: EventTypes.viewItemList;
};

export type DataLayerSelectedItemEvent = DataLayerEventBase & {
  selected_item_variant: number | string;
};

export type DataLayerSelectedItemEventV2 = {
  ecommerce: {
    items: DataLayerSelectItem[];
  };
  event: EventTypes.selectItem;
};

export type DataLayerAddToCartV2Event = {
  ecommerce: {
    items: DataLayerAddToCartItem[] | null;
  };
  event: EventTypes.addToCartV2;
};

export type DataLayerAddToCartV2OrRemoveFromCartV2Event = {
  ecommerce: {
    items: DataLayerAddToCartItem[] | null;
  };
  event: EventTypes.addToCartV2 | EventTypes.removeFromCartV2;
};

export type DataLayerFormSubmitQuotationEvent = {
  email: string;
  event: EventTypes.formSubmitQuotation;
  formLocation: FormLocationDataLayer;
  phoneNumber: string;
};

export type DataLayerEvent =
  | DataLayerSearchEvent
  | DataLayerProductClickEvent
  | DataLayerProductDetailEvent
  | DataLayerAddEvent
  | DataLayerRemoveEvent
  | DataLayerRedeemRemoveEvent
  | DataLayerCheckoutEvent
  | DataLayerSuccessfulOrderEvent
  | DataLayerLoginUserSuccessfulEvent
  | DataLayerSelectedItemEvent
  | DataLayerViewItemEvent
  | DataLayerAddShippingInfoEvent
  | DataLayerViewCartEvent
  | DataLayerPurchaseEvent
  | DataLayerAddPaymentInfoEvent
  | DatalayerBeginCheckoutEvent
  | DataLayerPageViewEvent
  | DataLayerViewItemListEvent
  | DataLayerSelectedItemEventV2
  | DataLayerAddToCartV2Event
  | DataLayerAddToCartV2OrRemoveFromCartV2Event
  | DataLayerFormSubmitQuotationEvent
  | DataLayerSearchBoxClickedEvent;
